import { useDroppable } from '@dnd-kit/core';
import { ListCard, Text } from '@chatsdet/ui';
import { DraggableCredentialRow } from './DraggableCredentialRow';
import type { TestUserCredential } from './types';

type Props = {
  propertyId: string;
  credentials: TestUserCredential[];
  /** Whether any persona groups exist (changes the helper copy). */
  hasPersonas: boolean;
  onEditCredential: (credId: string) => void;
  onDeleteCredential: (cred: TestUserCredential) => void;
  onClearStorageState: (credId: string) => void;
  onRefresh: () => void;
};

/**
 * Credentials not bound to any persona. Acts as a drop target: dragging a
 * credential here unlinks it from its persona.
 */
export function UnassignedSection({
  propertyId,
  credentials,
  hasPersonas,
  onEditCredential,
  onDeleteCredential,
  onClearStorageState,
  onRefresh,
}: Props) {
  const { setNodeRef, isOver } = useDroppable({ id: 'persona:none' });

  // Stay mounted (as a drop target) while personas exist, even when empty —
  // otherwise there'd be nowhere to drag a credential to unbind it.
  if (credentials.length === 0 && !hasPersonas) return null;

  if (credentials.length === 0) {
    return (
      <div
        ref={setNodeRef}
        data-dropzone={!isOver ? true : undefined}
        className={`rounded-lg border border-dashed px-4 py-3 transition-colors ${
          isOver ? 'border-primary bg-muted/40' : 'border-border'
        }`}
      >
        <Text size="sm" className="text-muted-foreground">
          No unassigned credentials — drop a credential here to unbind it from its persona.
        </Text>
      </div>
    );
  }

  return (
    <ListCard
      ref={setNodeRef}
      title="Unassigned credentials"
      titleAccessory={`${credentials.length}`}
      description={
        hasPersonas
          ? "Drag a credential into a persona to bind it, or assign one from the credential's edit form."
          : undefined
      }
      className={`transition-colors ${isOver ? 'border-primary ring-1 ring-primary/40' : ''}`}
    >
      {credentials.map((cred, index) => (
        <DraggableCredentialRow
          key={cred.id}
          propertyId={propertyId}
          credential={cred}
          sourceTemplateId={null}
          index={index}
          onEdit={() => onEditCredential(cred.id)}
          onDelete={() => onDeleteCredential(cred)}
          onClearStorageState={() => onClearStorageState(cred.id)}
          onRefresh={onRefresh}
        />
      ))}
    </ListCard>
  );
}
